/**
 * When automatic family texts may go out: weekdays, 9:00–18:00 Toronto time.
 *
 * A mirror of nestjs delivery-scheduling/send-window.util.ts, which is what
 * actually holds the texts back — this copy only lets a modal say so before
 * staff press the button. Change both together; the nestjs spec
 * (send-window.util.spec.ts) imports this file and fails if they disagree.
 *
 * No imports on purpose, so that spec can load it.
 */
const TIME_ZONE = 'America/Toronto';

export const FAMILY_SEND_WINDOW = {
  startHour: 9,
  endHour: 18,
  /** 0 = Sunday … 6 = Saturday. */
  weekdays: [1, 2, 3, 4, 5] as readonly number[],
} as const;

/** Mirrors FAMILY_SEND_HOLIDAYS in nestjs — empty unless we decide otherwise. */
export const FAMILY_SEND_HOLIDAYS: ReadonlySet<string> = new Set<string>();

const torontoParts = (now: Date) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour') };
};

const isoDay = (y: number, m: number, d: number) =>
  new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);

const isSendDay = (y: number, m: number, d: number) =>
  FAMILY_SEND_WINDOW.weekdays.includes(new Date(Date.UTC(y, m - 1, d)).getUTCDay()) &&
  !FAMILY_SEND_HOLIDAYS.has(isoDay(y, m, d));

/** True when `now` falls on a day families may be texted (a weekday). */
export const isFamilySendDay = (now: Date = new Date()): boolean => {
  const { year, month, day } = torontoParts(now);
  return isSendDay(year, month, day);
};

export const isWithinSendWindow = (now: Date = new Date()): boolean => {
  const { year, month, day, hour } = torontoParts(now);
  return (
    isSendDay(year, month, day) &&
    hour >= FAMILY_SEND_WINDOW.startHour &&
    hour < FAMILY_SEND_WINDOW.endHour
  );
};

/** `now` inside the window, otherwise 9:00 on the next send day. */
export const nextSendTime = (now: Date = new Date()): Date => {
  if (isWithinSendWindow(now)) return now;
  const { year, month, day, hour } = torontoParts(now);
  let offset = hour < FAMILY_SEND_WINDOW.startHour ? 0 : 1;
  while (offset < 32 && !isSendDay(year, month, day + offset)) offset++;
  const local = Date.UTC(year, month - 1, day + offset, FAMILY_SEND_WINDOW.startHour);
  // Shift by Toronto's offset from UTC at that instant (4h summer, 5h winter).
  const asToronto = new Date(new Date(local).toLocaleString('en-US', { timeZone: TIME_ZONE }));
  const asUtc = new Date(new Date(local).toLocaleString('en-US', { timeZone: 'UTC' }));
  return new Date(local + (asUtc.getTime() - asToronto.getTime()));
};

/** "9:00 a.m." today, otherwise "Monday 9:00 a.m." — for hint and toast text. */
export const describeSendTime = (sendAt: Date, now: Date = new Date()): string => {
  const time = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
  }).format(sendAt);
  const sameDay = (d: Date) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE }).format(d);
  if (sameDay(sendAt) === sameDay(now)) return time;
  const weekday = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    weekday: 'long',
  }).format(sendAt);
  return `${weekday} ${time}`;
};
